import { FormEvent, useCallback, useEffect, useState } from 'react'
import { Bot } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { taskService } from '@/services/task-service'
import { apiErrorMessage } from '@/services/team-service'
import type { TaskComment } from '@/types/tasks'
import * as toast from '@/lib/toast'

const dateTime = (value: string) =>
  new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

/** Histórico/notas da tarefa: autor e data; comentário do agente (Hermes) marcado. */
export function TaskComments({ taskId, refreshKey }: { taskId: number; refreshKey?: number }) {
  const [comments, setComments] = useState<TaskComment[]>([])
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)

  const load = useCallback(() => {
    taskService.getTaskComments(taskId).then(setComments).catch(() => setComments([]))
  }, [taskId])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!text.trim()) return
    setSending(true)
    try {
      await taskService.addTaskComment(taskId, text.trim())
      setText('')
      load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível comentar'))
    } finally {
      setSending(false)
    }
  }

  return (
    <section aria-label="Histórico" className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">Histórico</h3>
      {comments.length === 0 && <p className="text-sm text-muted-foreground">Sem notas ainda.</p>}
      <ul className="flex flex-col gap-2">
        {comments.map((c) => (
          <li key={c.id} className="rounded-md bg-muted/50 p-2 text-sm">
            <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{c.author_name}</span>
              {c.author_kind === 'AGENT' && (
                <Badge variant="secondary" className="gap-1"><Bot className="size-3" /> agente</Badge>
              )}
              <span>{dateTime(c.created_at)}</span>
            </div>
            <p className="whitespace-pre-line">{c.text}</p>
          </li>
        ))}
      </ul>
      <form className="flex flex-col gap-2" onSubmit={submit}>
        <Textarea aria-label="Nova nota" rows={2} maxLength={500} placeholder="Escrever nota…" value={text} onChange={(e) => setText(e.target.value)} />
        <Button type="submit" size="sm" variant="outline" className="self-end" disabled={sending || !text.trim()}>Adicionar nota</Button>
      </form>
    </section>
  )
}
